// Buckets orchestrator graph nodes into coarse UI phases so the conversation
// panel can show real streaming progress instead of a fixed cycling timer.
//
// Ported from the ai-v4 reference (`frontend/src/components/chat/streaming-progress.ts`,
// PR wavvest/wavvest-ai-v4#51). Ground truth for node names is the weave
// orchestrator graph (`backend/src/weave_orchestrator/graph.py` in ai-v4).
// The node -> phase map should be re-verified against real weave-x `updates`
// frames, since weave-x may rename or wrap v4 graph nodes.

import type { ProgressPhase } from '@/common/components/wavvest-planning/types';

export const NODE_PHASE: Record<string, ProgressPhase> = {
  // Phase 1 — intake, preprocessing, source resolution.
  message_compactor: 1,
  intent_recognition: 1,
  new_plan_clearing: 1,
  staged_files_gate: 1,
  preprocessing_dispatch: 1,
  additional_context_processor: 1,
  prompt_processor: 1,
  file_processor: 1,
  aggregated_sources: 1,
  configuration_manager: 1,
  source_conflict_resolution: 1,
  canon_scribe: 1,
  chatterbox: 1,

  // Phase 2 — orchestration and advisor analysis.
  orchestrator: 2,
  context_resolver: 2,
  retirement_income_advisor: 2,
  tax_advisor: 2,
  investment_advisor: 2,
  estate_insurance_advisor: 2,
  education_debt_advisor: 2,
  compensation_advisor: 2,
  join_advisors: 2,

  // Phase 3 — report generation and release.
  report_generator: 3,
  render_validation: 3,
  release_gate: 3,
};

export const PHASE_LABELS: Record<ProgressPhase, string[]> = {
  1: ['Analyzing your scenario...', 'Reviewing your details...', 'Checking your sources...'],
  2: ['Running the analysis...', 'Consulting advisors...', 'Crunching the numbers...'],
  3: ['Generating your plan...', 'Polishing the details...', 'Almost there...'],
};

/**
 * Returns the highest phase among the given node names, or `null` when none of
 * them map to a known phase. Callers combine this with the running phase so the
 * displayed progress only ever advances (see `advanceProgressPhase` reducer).
 */
export function phaseForNodeNames(nodeNames: string[]): ProgressPhase | null {
  let max: ProgressPhase | null = null;
  for (const name of nodeNames) {
    const phase = NODE_PHASE[name];
    if (phase && (max === null || phase > max)) {
      max = phase;
    }
  }
  return max;
}
